import { useEffect } from 'react';
import { AlertCircle, Download, Loader2, RotateCcw, Mic } from 'lucide-react';
import {
  useColaMedia,
  recuperarGrabacionesInterrumpidas,
  type MediaEnCola,
} from '../lib/colaMedia';

/**
 * AUDIOS QUE TODAVÍA NO ENTRARON AL ACTA — visibles, nunca perdidos.
 *
 * La cola vive en IndexedDB y sobrevive la recarga, el cambio de app y que el
 * navegador mate la pestaña. Pero que sobreviva en silencio no alcanza: quien
 * grabó necesita VER que su voz sigue ahí y que se está reintentando, porque
 * la experiencia del 6-ago fue exactamente la contraria —el envío fallaba y no
 * quedaba rastro de nada—.
 *
 * Para lo que el servidor rechazó de forma definitiva se ofrece DESCARGAR el
 * audio al teléfono: si la plataforma no puede recibirlo, al menos la persona
 * se queda con su grabación.
 */
export function ColaAudio() {
  const items = useColaMedia((s) => s.items);
  const vaciando = useColaMedia((s) => s.vaciando);
  const flush = useColaMedia((s) => s.flush);
  const descartar = useColaMedia((s) => s.descartar);
  const reintentar = useColaMedia((s) => s.reintentar);
  const refrescar = useColaMedia((s) => s.refrescar);

  // Al montar (incluido tras un REFRESCO de página), releer IndexedDB: sin esto
  // el store arranca vacío y una grabación guardada —p. ej. la del micrófono de
  // sala que rebotó— quedaba invisible aunque siguiera a salvo en el disco. Y
  // reensamblar cualquier grabación que quedó a medio cerrar.
  useEffect(() => {
    void recuperarGrabacionesInterrumpidas().finally(() => void refrescar());
  }, [refrescar]);

  if (items.length === 0) return null;

  const esperando = items.filter((i) => !i.bloqueado);
  const bloqueados = items.filter((i) => i.bloqueado);

  return (
    <section
      className="mx-4 mt-4 rounded-lg border border-border-strong bg-surface-sunken px-3 py-2.5"
      aria-live="polite"
    >
      <p className="flex items-center gap-2 font-sans text-sm font-semibold text-content">
        <Mic className="h-4 w-4 shrink-0 text-cyan" aria-hidden="true" />
        {esperando.length > 0
          ? `${esperando.length} ${esperando.length === 1 ? 'grabación guardada' : 'grabaciones guardadas'} en este teléfono`
          : 'Grabaciones guardadas en este teléfono'}
      </p>

      {esperando.length > 0 && (
        <>
          <p className="mt-0.5 font-sans text-xs text-content-muted">
            Tu voz no se perdió. Se sigue enviando sola —también si cerrás y
            volvés a abrir— y entra al acta con la hora en que la dijiste.
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {esperando.map((it) => (
              <li
                key={it.idem}
                className="flex items-center justify-between gap-2 font-sans text-xs text-content-muted"
              >
                <span className="truncate">
                  {horaDe(it.inicio)} · {formatoDuracion(it.duracionS)}
                  {it.recuperada && (
                    <span className="ml-1 text-success">· rescatada</span>
                  )}
                </span>
                <span className="shrink-0">
                  {vaciando ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    `${it.intentos} ${it.intentos === 1 ? 'intento' : 'intentos'}`
                  )}
                </span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => void flush()}
            disabled={vaciando}
            className="mt-2 flex min-h-touch items-center gap-1.5 rounded-md font-sans text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Intentar ahora
          </button>
        </>
      )}

      {bloqueados.length > 0 && (
        <div className="mt-2 border-t border-border pt-2">
          <p className="flex items-start gap-1.5 font-sans text-xs text-warning">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {bloqueados.length === 1
              ? 'Una grabación no pudo entrar al acta.'
              : `${bloqueados.length} grabaciones no pudieron entrar al acta.`}{' '}
            Descargala para no perderla y avisá a quien facilita.
          </p>
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {bloqueados.map((it) => (
              <li key={it.idem} className="flex flex-wrap items-center gap-2">
                <span className="font-sans text-xs text-content-muted">
                  {horaDe(it.inicio)} · {formatoDuracion(it.duracionS)} · {it.bloqueado}
                </span>
                <button
                  type="button"
                  onClick={() => void reintentar(it.idem)}
                  disabled={vaciando}
                  className="flex min-h-touch items-center gap-1 rounded-md font-sans text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  Reintentar
                </button>
                <button
                  type="button"
                  onClick={() => descargar(it)}
                  className="flex min-h-touch items-center gap-1 rounded-md font-sans text-xs font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:shadow-focus"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                  Descargar
                </button>
                <button
                  type="button"
                  onClick={() => void descartar(it.idem)}
                  className="flex min-h-touch items-center rounded-md font-sans text-xs text-content-muted hover:underline focus-visible:outline-none focus-visible:shadow-focus"
                >
                  Descartar
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** Guarda el audio en el teléfono: si el acta no lo recibe, la persona sí. */
function descargar(it: MediaEnCola): void {
  const url = URL.createObjectURL(it.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = it.filename || 'grabacion';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Dar tiempo al navegador a iniciar la descarga antes de soltar el blob.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function horaDe(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'hora desconocida';
  return d.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' });
}

function formatoDuracion(s: number): string {
  const m = Math.floor(s / 60);
  const seg = Math.floor(s % 60);
  return `${m}:${String(seg).padStart(2, '0')}`;
}
